import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

interface Bookmark {
  id: string;
  provider_id: string;
  created_at: string;
}

export function useBookmarks() {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchBookmarks();
    } else {
      setBookmarks([]);
      setIsLoading(false);
    }
  }, [user]);

  const fetchBookmarks = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("provider_bookmarks")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBookmarks(data || []);
    } catch (error) {
      console.error("Error fetching bookmarks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isBookmarked = (providerId: string) => {
    return bookmarks.some(b => b.provider_id === providerId);
  };

  const addBookmark = async (providerId: string) => {
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .from("provider_bookmarks")
        .insert({
          user_id: user.id,
          provider_id: providerId,
        })
        .select()
        .single();

      if (error) throw error;
      setBookmarks(prev => [data, ...prev]);
      return true;
    } catch (error) {
      console.error("Error adding bookmark:", error);
      return false;
    }
  };

  const removeBookmark = async (providerId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("provider_bookmarks")
        .delete()
        .eq("user_id", user.id)
        .eq("provider_id", providerId);

      if (error) throw error;
      setBookmarks(prev => prev.filter(b => b.provider_id !== providerId));
      return true;
    } catch (error) {
      console.error("Error removing bookmark:", error);
      return false;
    }
  };

  return {
    bookmarks,
    isLoading,
    isBookmarked,
    addBookmark,
    removeBookmark,
    refetch: fetchBookmarks,
  };
}
